import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Car, Check, EyeOff, Trash2, RefreshCw, Loader2, MessageSquareReply, Save, CornerDownRight, ExternalLink } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const BADGE = {
  pending: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  approved: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  rejected: "border-white/15 bg-white/5 text-white/50",
};
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

// Unapproved photos are private, so load them with the admin token
const AuthImg = ({ path, token, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${BACKEND}${path}`, { responseType: "blob", headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => url && URL.revokeObjectURL(url);
  }, [path, token]);
  if (!src) return <div className="flex h-full w-full items-center justify-center bg-white/5"><Loader2 className="h-4 w-4 animate-spin text-white/40" /></div>;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block h-full w-full">
      <img src={src} alt={alt} className="h-full w-full object-cover transition-transform duration-500 hover:scale-110" />
    </a>
  );
};

const ReplyEditor = ({ r, token, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.reply?.text || "");
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const save = async () => {
    if (!text.trim()) { toast.error("Write a reply first"); return; }
    setSaving(true);
    try { await axios.post(`${API}/admin/reviews/${r.id}/reply`, { text }, auth); toast.success("Reply posted publicly"); setEditing(false); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save reply"); } finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try { await axios.delete(`${API}/admin/reviews/${r.id}/reply`, auth); toast.success("Reply removed"); setText(""); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't remove reply"); } finally { setSaving(false); }
  };
  if (!editing && r.reply?.text) {
    return (
      <div className="mt-3 rounded-lg border-l-2 border-brand bg-brand/[0.06] p-3" data-testid={`admin-reply-${r.id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.06em] text-brand"><CornerDownRight className="h-3.5 w-3.5" />Your public reply</span>
          <div className="flex gap-3 text-[12px] font-semibold">
            <button onClick={() => setEditing(true)} className="text-white/60 hover:text-white" data-testid={`edit-reply-${r.id}`}>Edit</button>
            <button disabled={saving} onClick={remove} className="text-white/60 hover:text-brand" data-testid={`remove-reply-${r.id}`}>Remove</button>
          </div>
        </div>
        <p className="mt-1.5 whitespace-pre-line text-sm text-white/75">{r.reply.text}</p>
      </div>
    );
  }
  if (!editing) {
    return <button onClick={() => setEditing(true)} className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-brand" data-testid={`reply-review-${r.id}`}><MessageSquareReply className="h-4 w-4" />Reply publicly</button>;
  }
  return (
    <div className="mt-3">
      <Textarea rows={3} maxLength={600} value={text} onChange={(e) => setText(e.target.value)} placeholder={`Thanks ${r.name.split(" ")[0]}! ...`}
        className="rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0" data-testid={`reply-input-${r.id}`} />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-white/35">{text.length}/600 · shown publicly as EVTTuning UK</span>
        <div className="flex gap-2">
          <button onClick={() => { setEditing(false); setText(r.reply?.text || ""); }} className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/70 hover:border-white/40">Cancel</button>
          <button disabled={saving} onClick={save} className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#d0141c] disabled:opacity-60" data-testid={`save-reply-${r.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Post reply</button>
        </div>
      </div>
    </div>
  );
};

const GoogleLinkSettings = ({ token }) => {
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/settings`).then(({ data }) => { setUrl(data.google_review_url || ""); setSaved(data.google_review_url || ""); }).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, { google_review_url: url }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(data.google_review_url); toast.success(data.google_review_url ? "Google review link saved" : "Google review button removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save link"); } finally { setSaving(false); }
  };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#111217] p-5" data-testid="google-link-settings">
      <div className="flex items-center gap-2 font-semibold text-white"><span className="font-display text-lg font-bold text-[#4285F4]">G</span>Google review link</div>
      <p className="mt-1 text-sm text-white/55">Paste your link from Google Business Profile → <span className="text-white/80">Ask for reviews</span>. It adds a "Review us on Google" button to the site and to every booking confirmation email.</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/.../review"
          className="h-11 flex-1 rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0" data-testid="google-link-input" />
        <button disabled={saving || url === saved} onClick={save} className="btn-red flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="google-link-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</button>
      </div>
      {saved && <a href={saved} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-white/50 hover:text-brand">Test your link <ExternalLink className="h-3 w-3" /></a>}
    </div>
  );
};

const AdminReviews = ({ token, onUnauthorized, onCounts }) => {
  const [tab, setTab] = useState("approved");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/reviews`, { params: tab === "all" ? {} : { status: tab }, headers: { Authorization: `Bearer ${token}` } });
      setData(d);
      onCounts?.(d.counts);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
    } finally { setLoading(false); }
  }, [tab, token, onUnauthorized, onCounts]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status, msg) => {
    setBusy(r.id);
    try {
      await axios.post(`${API}/admin/reviews/${r.id}/status`, { status }, auth);
      toast.success(msg);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); } finally { setBusy(null); }
  };
  const remove = async (r) => {
    setBusy(r.id);
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`, auth);
      toast.success("Review deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); } finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold text-white">Reviews</h1>
          <p className="mt-1 text-white/55">Reviews go live as soon as they're posted. Hide or delete any you don't want on the site.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/40" data-testid="reviews-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      <GoogleLinkSettings token={token} />
      <div className="mt-8 grid grid-cols-2 gap-4 sm:max-w-md">
        {[["approved", "Live on site", "text-emerald-300"], ["rejected", "Hidden", "text-white/60"]].map(([k, l, c]) => (
          <div key={k} className="rounded-xl border border-white/10 bg-[#111217] p-5">
            <div className="text-[12px] uppercase tracking-[0.1em] text-white/45">{l}</div>
            <div className={`mt-1 font-display text-3xl font-bold ${c}`} data-testid={`r-count-${k}`}>{data.counts?.[k] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-2">
        {[["approved", "Live"], ["rejected", "Hidden"], ["all", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-brand bg-brand text-white" : "border-white/15 text-white/70 hover:text-white"}`} data-testid={`r-tab-${k}`}>{l}</button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {data.items.map((r) => (
          <div key={r.id} className="flex flex-col rounded-2xl border border-white/10 bg-[#111217] p-5" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-xl font-bold text-white">{r.name}</span>
                  <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase ${BADGE[r.status]}`}>{r.status === "rejected" ? "hidden" : "live"}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[13px] text-white/50"><Car className="h-3.5 w-3.5 text-brand" />{r.car} · {fmt(r.created_at)}</div>
              </div>
              <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((k) => <Star key={k} className={`h-4 w-4 ${k <= r.rating ? "fill-brand text-brand" : "text-white/20"}`} />)}</div>
            </div>
            <p className="mt-4 rounded-lg bg-white/[0.03] p-3 text-[15px] leading-relaxed text-white/80">"{r.text}"</p>
            {r.photos.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {r.photos.map((p, k) => <div key={p} className="aspect-square overflow-hidden rounded-lg border border-white/10"><AuthImg path={p} token={token} alt={`${r.car} ${k + 1}`} /></div>)}
              </div>
            )}
            <ReplyEditor key={`${r.id}-${r.reply?.at || "none"}`} r={r} token={token} onSaved={load} />
            <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
              {r.status !== "approved" && (
                <button disabled={busy === r.id} onClick={() => setStatus(r, "approved", "Shown on the site again")} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60" data-testid={`approve-review-${r.id}`}><Check className="h-4 w-4" />Show again</button>
              )}
              {r.status !== "rejected" && (
                <button disabled={busy === r.id} onClick={() => setStatus(r, "rejected", "Hidden from the site")} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-white/40 disabled:opacity-60" data-testid={`hide-review-${r.id}`}><EyeOff className="h-4 w-4" />Hide</button>
              )}
              <button disabled={busy === r.id} onClick={() => setToDelete(r)} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/70 transition-colors hover:border-brand hover:text-brand disabled:opacity-60" data-testid={`delete-review-${r.id}`}><Trash2 className="h-4 w-4" />Delete</button>
            </div>
          </div>
        ))}
      </div>
      {!loading && data.items.length === 0 && <div className="mt-6 rounded-2xl border border-dashed border-white/10 p-12 text-center text-white/45" data-testid="reviews-empty">No reviews here yet.</div>}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-[#111217] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete this review?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">The review and its photos will be permanently removed. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">Keep</AlertDialogCancel>
            <AlertDialogAction className="bg-brand text-white hover:bg-[#d0141c]" onClick={() => { const r = toDelete; setToDelete(null); remove(r); }} data-testid="confirm-delete-review">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminReviews;
